import { URLMeta, rewriteUrl, unrewriteUrl } from "@rewriters/url";
import { ScramjetContext } from "@/shared";
import { String } from "@/shared/snapshot";

export function rewriteCss(
	css: string,
	context: ScramjetContext,
	meta: URLMeta
) {
	return handleCss("rewrite", css, context, meta);
}

export function unrewriteCss(css: string, context: ScramjetContext) {
	return handleCss("unrewrite", css, context);
}

// The address in url(...) as CSS reads it: escapes like "\2f " (a slash, as AEM writes them
// in Eurowings' styles) were taken literally, and the picture was asked for at "2f%20content/...".
const CSS_ESCAPE = /\\(?:([0-9a-fA-F]{1,6})[ \t\n\r\f]?|([\s\S]))/g;
function cssUnescape(value: string) {
	if (!value.includes("\\")) return value;

	return value.replace(CSS_ESCAPE, (_match, hex: string | undefined, char: string) => {
		if (hex) {
			const point = parseInt(hex, 16);
			const valid = point > 0 && point <= 0x10ffff && !(point >= 0xd800 && point <= 0xdfff);

			return globalThis.String.fromCodePoint(valid ? point : 0xfffd);
		}

		return char === "\n" ? "" : char;
	});
}

// Characters the rewritten address may not carry as they are, by how it is written.
function cssEscape(value: string, quote: string) {
	const special = quote ? new RegExp("[\\\\\\n" + quote + "]", "g") : /[\s"'()\\]/g;

	return value.replace(special, (c) => "\\" + c.charCodeAt(0).toString(16) + " ");
}

function handleCss(
	type: "rewrite" | "unrewrite",
	css: string,
	context: ScramjetContext,
	meta?: URLMeta
) {
	// regex from vk6 (https://github.com/ading2210). A url( that the text ends in before its ")"
	// still loads in browsers (CSS closes it at the end), and Fandom's inline styles end that
	// way: the end of the text counts as the closing bracket.
	const urlRegex =
		/(?i:url)\((?:\s*"((?:\\.|[^"])+)"\s*|\s*'((?:\\.|[^'])+)'\s*|((?!\s*['"])(?!\s*\))(?:\\.|[^)])+?))(?:\)|$(?![\s\S]))/gm;
	// No space is needed before a quoted address: minifiers write @import"https://..." (a
	// Handelsblatt widget did, and its font styles came straight from the site).
	const Atruleregex =
		/@import(?:\s+|(?=['"]))((?i:url)\s*?\(.{0,9999}?\)|['"].{0,9999}?['"]|.{0,9999}?)($|\s|;)/gm;
	css = String(css);
	css = css.replace(
		urlRegex,
		(
			match,
			doubleQuotedUrl: string | undefined,
			singleQuotedUrl: string | undefined,
			unquotedUrl: string | undefined
		) => {
			const url = doubleQuotedUrl ?? singleQuotedUrl ?? unquotedUrl;
			const quote = doubleQuotedUrl !== undefined ? '"' : singleQuotedUrl !== undefined ? "'" : "";
			const encodedUrl =
				type === "rewrite"
					? url.includes("\\")
						? cssEscape(rewriteUrl(cssUnescape(url.trim()), context, meta!), quote)
						: rewriteUrl(url.trim(), context, meta!)
					: unrewriteUrl(url.trim(), context);

			return match.replace(url, encodedUrl);
		}
	);
	css = css.replace(Atruleregex, (match, importStatement: string) => {
		return match.replace(
			importStatement,
			importStatement.replace(
				/^(url\(['"]?|['"]|)(.+?)(['"]|['"]?\)|)$/gm,
				(match: string, firstQuote: string, url: string, endQuote: string) => {
					if (firstQuote.startsWith("url")) {
						return match;
					}
					const encodedUrl =
						type === "rewrite"
							? rewriteUrl(url.trim(), context, meta!)
							: unrewriteUrl(url.trim(), context);

					return `${firstQuote}${encodedUrl}${endQuote}`;
				}
			)
		);
	});

	// image-set() also takes addresses as plain strings, without url(): Substack's backgrounds
	// are image-set('https://...' 1x type('image/avif'), ...), and they loaded straight from
	// the site. The strings in it are rewritten, except the arguments of type().
	css = handleImageSet(css, (url: string, quote: string) => {
		if (type === "rewrite") {
			const raw = cssUnescape(url.trim());

			return cssEscape(rewriteUrl(raw, context, meta!), quote);
		}

		return cssEscape(unrewriteUrl(cssUnescape(url.trim()), context), quote);
	});

	return css;
}

const IMAGE_SET = /(?:-webkit-)?image-set\(/gi;
function handleImageSet(css: string, change: (url: string, quote: string) => string) {
	if (!/image-set\(/i.test(css)) return css;
	let out = "";
	let last = 0;
	IMAGE_SET.lastIndex = 0;
	let match: RegExpExecArray | null;
	while ((match = IMAGE_SET.exec(css))) {
		const start = match.index + match[0].length;
		let depth = 1;
		let i = start;
		let quote = "";
		for (; i < css.length && depth > 0; i++) {
			const c = css[i];
			if (quote) {
				if (c === "\\") i++;
				else if (c === quote) quote = "";
				continue;
			}
			if (c === '"' || c === "'") quote = c;
			else if (c === "(") depth++;
			else if (c === ")") depth--;
		}
		const end = depth === 0 ? i - 1 : i;
		const inner = css.slice(start, end);
		const changed = inner.replace(
			/(["'])((?:\\[\s\S]|(?!\1)[^\\])*)\1/g,
			(all: string, q: string, value: string, offset: number) => {
				const before = inner.slice(0, offset).replace(/\s+$/, "");
				if (/(?:type|url)\($/i.test(before)) return all;

				return q + change(value, q) + q;
			}
		);
		out += css.slice(last, start) + changed;
		last = end;
		IMAGE_SET.lastIndex = end;
	}

	return out + css.slice(last);
}
